// JOBE-0044.11: AddToCollectionAction — inspector affordance for
// PUT /api/collections/{slug}/members/{document_urn}.
//
// Renders as a disclosure button (`Add to collection ↓`) that toggles
// an inline popover listing active collections. Each row whose URN
// already appears in `entity.collections[]` is disabled with a
// "already in" suffix. Selecting an enabled row issues the PUT and
// optimistically flips the row to the disabled state so chained adds
// don't double-fire.
//
// Reads the collections list from CollectionsContext; when no provider
// is mounted (e.g. tests with the bare preview), the action degrades
// to a stub that renders nothing — the rail's local-fetch fallback is
// the responsibility of CollectionsRailBlock, not the inspector.

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CollectionsContext, COLLECTIONS_INITIAL } from '../CollectionsContext.js';

export function AddToCollectionAction({ entity }) {
  const ctx = useContext(CollectionsContext);
  const providerMounted = ctx !== COLLECTIONS_INITIAL;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // slug currently being PUT
  const [error, setError] = useState(null);
  // localMembership augments entity.collections[] with optimistic adds
  // so the disabled-state flips immediately. Keyed by URN.
  const [localMembership, setLocalMembership] = useState(() => new Set());
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const memberUrns = useMemo(() => {
    const s = new Set(localMembership);
    const existing = Array.isArray(entity?.collections) ? entity.collections : [];
    for (const c of existing) {
      if (c?.urn) s.add(c.urn);
    }
    return s;
  }, [entity, localMembership]);

  if (!providerMounted) return null;
  if (!entity?.urn) return null;

  const items = Array.isArray(ctx.items) ? ctx.items : [];

  const onPick = async (collection) => {
    const slug = collection?.slug;
    if (!slug) return;
    if (memberUrns.has(collection.urn)) return;
    setBusy(slug);
    setError(null);
    try {
      const r = await fetch(
        `/api/collections/${encodeURIComponent(slug)}/members/${encodeURIComponent(entity.urn)}`,
        {
          method: 'PUT',
          credentials: 'same-origin',
        },
      );
      if (r.ok) {
        setLocalMembership((prev) => {
          const next = new Set(prev);
          next.add(collection.urn);
          return next;
        });
        setBusy(null);
        return;
      }
      let payload = null;
      try { payload = await r.json(); } catch { /* tolerate */ }
      setError({
        slug,
        status: r.status,
        code: payload?.error || null,
        message: payload?.message || null,
      });
      setBusy(null);
    } catch (err) {
      setError({ slug, status: 0, message: err?.message || String(err) });
      setBusy(null);
    }
  };

  return (
    <div className="library-inspector-add-collection">
      <button
        type="button"
        className="library-inspector-add-collection-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="add-to-collection-toggle"
      >
        Add to collection {open ? '↑' : '↓'}
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="library-inspector-add-collection-popover"
          data-testid="add-to-collection-popover"
        >
          {ctx.status === 'loading' && (
            <p className="library-inspector-add-collection-empty">loading…</p>
          )}
          {ctx.status === 'error' && (
            <p className="library-inspector-add-collection-empty">
              couldn't load collections
            </p>
          )}
          {ctx.status === 'ready' && items.length === 0 && (
            <p className="library-inspector-add-collection-empty">
              No collections.
            </p>
          )}
          {ctx.status === 'ready' && items.length > 0 && (
            <ul className="library-inspector-add-collection-list">
              {items.map((c) => {
                const isMember = memberUrns.has(c.urn);
                const isBusy = busy === c.slug;
                return (
                  <li key={c.urn}>
                    <button
                      type="button"
                      className="library-inspector-add-collection-row"
                      onClick={() => onPick(c)}
                      disabled={isMember || isBusy}
                      data-testid={`add-to-collection-row-${c.slug}`}
                    >
                      <span className="library-inspector-add-collection-name">
                        {c.name || c.slug}
                      </span>
                      {isMember && (
                        <span className="library-inspector-add-collection-suffix">
                          · already in
                        </span>
                      )}
                      {isBusy && !isMember && (
                        <span className="library-inspector-add-collection-suffix">
                          · adding…
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {error && (
            <p
              className="library-inspector-add-collection-error"
              data-testid="add-to-collection-error"
            >
              couldn't add to {error.slug}
              {error.code ? ` (${error.code})` : ` (${error.status || 'network'})`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
